import React from "react";
import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import { isActivePendingFlag } from "../utils/recruiterOwnership";

const PANEL_BORDER = "#E9D8FD";
const PANEL_BACKGROUND = "#FAF5FF";
const TASK_ACCENT = "#7C3AED";
const TASK_TITLE = "#5B21B6";
const TASK_MUTED = "#6B7280";

export function getEntityBackgroundTasks(entity = {}) {
  const tasks = [];

  if (isActivePendingFlag(entity?.contact_repair_pending)) {
    tasks.push({
      key: "contact-repair",
      label: "Refreshing company contacts",
      detail:
        "AI is finding team and leadership contacts, then filling in email, phone, LinkedIn, and profile details.",
    });
  }

  if (isActivePendingFlag(entity?.company_profile_enrichment_pending)) {
    tasks.push({
      key: "company-profile-enrichment",
      label: "Enriching company profile",
      detail:
        "AI is collecting the company description, mission, products, competitors, and other overview details in the background.",
    });
  }

  if (isActivePendingFlag(entity?.job_details_enrichment_pending)) {
    tasks.push({
      key: "job-details-enrichment",
      label: "Enriching job details",
      detail:
        "AI is expanding the description, responsibilities, and qualifications fields for this job.",
    });
  }

  return tasks;
}

function formatInlineTaskLabel(tasks = []) {
  const safeTasks = Array.isArray(tasks) ? tasks.filter(Boolean) : [];
  if (!safeTasks.length) return "";

  const [firstTask] = safeTasks;
  const remainingCount = Math.max(0, safeTasks.length - 1);

  return remainingCount > 0
    ? `${firstTask.label} +${remainingCount} more`
    : firstTask.label;
}

export function InlineBackgroundTaskLabel({ entity, sx = {} }) {
  const tasks = getEntityBackgroundTasks(entity);
  if (!tasks.length) return null;

  return (
    <Stack
      direction="row"
      spacing={0.65}
      alignItems="center"
      title={tasks.map((task) => task.label).join("\n")}
      sx={{ mt: 0.45, minWidth: 0, ...sx }}
    >
      <CircularProgress
        size={11}
        thickness={6}
        sx={{ color: TASK_ACCENT, flexShrink: 0 }}
      />
      <Typography
        sx={{
          fontSize: 11.5,
          fontWeight: 700,
          color: TASK_ACCENT,
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {formatInlineTaskLabel(tasks)}
      </Typography>
    </Stack>
  );
}

export default function EntityBackgroundTasks({
  entity,
  title = "Background Tasks Running",
  sx = {},
}) {
  const tasks = getEntityBackgroundTasks(entity);
  if (!tasks.length) return null;

  return (
    <Box
      sx={{
        mb: 2,
        border: `1px solid ${PANEL_BORDER}`,
        borderRadius: 3,
        backgroundColor: PANEL_BACKGROUND,
        // Keep the banner copy aligned with the company tab section titles.
        px: { xs: 2, sm: 3, md: 4 },
        py: 1.5,
        ...sx,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.1 }}>
        <CircularProgress size={16} thickness={5} sx={{ color: TASK_ACCENT }} />
        <Typography sx={{ fontSize: 13, fontWeight: 800, color: TASK_TITLE }}>
          {title}
        </Typography>
      </Stack>

      <Stack
        spacing={0.9}
        // Match task copy to the title text, not the progress icon's edge.
        sx={{ pl: 3 }}
      >
        {tasks.map((task) => (
          <Box key={task.key}>
            <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: TASK_TITLE }}>
              {task.label}
            </Typography>
            {task.detail ? (
              <Typography sx={{ mt: 0.2, fontSize: 12, color: TASK_MUTED, lineHeight: 1.45 }}>
                {task.detail}
              </Typography>
            ) : null}
          </Box>
        ))}
      </Stack>
    </Box>
  );
}
